<template>
  <div v-if="notifications.length" class="urgent-notifications-card">
    <div class="urgent-notifications-steps">
      <div v-if="notLastStep" class="step">
        <font-awesome-icon
          :icon="['fal', 'bell-exclamation']"
          class="step-icon"
        />
        <span>{{ step + 1 }}/{{ count }}</span>
      </div>
      <div v-else class="step">
        <font-awesome-icon :icon="['fal', 'check']" class="step-icon" />
      </div>
    </div>
    <div class="urgent-notifications-body" :class="{ extand }">
      <div class="transition-wrapper">
        <UrgentNotificationSlide
          class="transition-slide"
          :style="slideTransition"
          :not-last-slide="notLastSlide"
          :title="notLastSlide ? notifications[slideStep].metadata.label : ''"
          :message="notLastSlide ? notifications[slideStep].metadata.body : ''"
        />
        <UrgentNotificationSlide
          class="current-slide"
          :not-last-slide="notLastStep"
          :slide-nbr="notLastStep ? step : -1"
          :title="notLastStep ? notifications[step].metadata.label : ''"
          :message="notLastStep ? notifications[step].metadata.body : ''"
          :has-linked-entity="
            !!notifications[step] && !!notifications[step].metadata.entityId
          "
          @reach-content="goToContent()"
          @reset="reset()"
        />
      </div>
      <base-button
        class="btn-collapse btn-urgente"
        type="danger"
        size="sm"
        @click="handleExtand()"
        >{{ $t('mayday-web.urgent-notification.expand') }}
        <font-awesome-icon
          :icon="['fal', extand ? 'chevron-up' : 'chevron-down']"
      /></base-button>
    </div>
    <div v-if="count > 1" class="urgent-notifications-nav">
      <base-button
        class="urgent-notifications-nav__btn btn-urgent"
        type="danger"
        size="sm"
        :disabled="step === 0"
        @click="handlePrev()"
        ><font-awesome-icon :icon="['fal', 'chevron-up']" class="chevron-icon"
      /></base-button>
      <base-button
        class="urgent-notifications-nav__btn btn-urgent"
        type="danger"
        size="sm"
        :disabled="step === count"
        @click="handleNext()"
        ><font-awesome-icon
          :icon="['fal', 'chevron-down']"
          class="chevron-icon"
      /></base-button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import UrgentNotificationSlide from '@/components/Notifications/UrgentNotificationSlide';

export default {
  name: 'urgent-notifications-card',
  components: {
    UrgentNotificationSlide,
  },
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      extand: false,
      step: 0,
      slideStep: 0,
      slideTransition: 'transform: translateY(-100px);',
    };
  },
  computed: {
    count() {
      return this.notifications.length;
    },
    notLastStep() {
      return !!this.notifications[this.step] && this.step < this.count;
    },
    notLastSlide() {
      return (
        !!this.notifications[this.slideStep] && this.slideStep < this.count
      );
    },
  },
  methods: {
    handleExtand() {
      this.extand = !this.extand;
      if (this.extand) this.slideTransition = 'transform: translateY(-182px);';
    },
    handlePrev() {
      this.slideStep = this.step;
      this.slideTransition = 'transform: translateY(0px);';
      setTimeout(() => {
        this.step -= 1;
        this.slideTransition = `transform: translateY(-${
          this.extand ? '182' : '100'
        }px); transition: transform 0.2s ease-in-out;`;
      }, 10);
    },
    handleNext() {
      const nextStep = this.step + 1;
      this.nextStep(nextStep);
    },
    reset() {
      const nextStep = 0;
      this.nextStep(nextStep);
    },
    nextStep(nextStep) {
      this.slideStep = nextStep;
      this.slideTransition =
        'transform: translateY(0px); transition: transform 0.2s ease-in-out;';
      setTimeout(() => {
        this.step = nextStep;
        this.slideTransition = `transform: translateY(-${
          this.extand ? '182' : '100'
        }px);`;
      }, 210);
    },
    async goToContent() {
      const { entityId, entityType } = this.notifications[this.step].metadata;
      const target = 'web';

      try {
        const path = await this.createRedirectUrl({
          entityId,
          entityType,
          target,
        });

        this.$router.push({
          path,
          query: {
            from: 'notification',
          },
        });
      } catch (e) {
        this.$message({
          message: this.$t('notifications.info.content-error'),
          type: 'error',
        });
      }
    },
    ...mapActions(['createRedirectUrl']),
  },
};
</script>

<style lang="scss" scoped>
.urgent-notifications {
  &-card {
    display: flex;
    background-color: white;
    border-radius: 4px;
  }

  &-steps {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: none;
    width: 80px;
    font-size: 28px;
    line-height: 34px;
    font-weight: 700;
    color: white;
    background-color: $red-mayday;
    border-radius: 4px 0 0 4px;

    .step {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .step-icon {
      font-size: 36px;
    }
  }

  &-body {
    height: 100px;
    position: relative;
    flex: 1 1 0%;
    transition: all 0.2s linear;

    &.extand {
      height: 182px;
      transition: all 0.2s linear;

      :deep() .content {
        height: 149px;

        .title-wrapper {
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
        }

        .title {
          &.sized {
            width: 100%;
          }
        }
      }

      .transition-slide {
        height: 182px;
      }

      .btn-collapse {
        color: $grey-8-mayday;
        background-color: white;
        &:hover {
          background-color: $grey-3-mayday;
        }

        &:active {
          background-color: $grey-4-mayday;
        }
      }
    }
  }

  &-nav {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    padding: 0 12px;

    &__btn {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 22px;
      height: 22px;
      margin: 0;
      font-size: 12px;
      line-height: 12px;
      border-radius: 4px;
    }
  }
}

.transition-wrapper {
  position: relative;
  overflow-y: hidden;
  padding: 16.5px 0px;
}

.transition-slide {
  position: absolute;
  overflow-y: hidden;
  height: 100px;
  inset: 0;
  z-index: 1;
  background-color: white;
  padding: 16.5px 0px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  font-size: 12px;
  line-height: 14.4px;
  font-weight: 700;
  border-radius: 2px;
  border: none;
}

.btn-collapse {
  position: absolute;
  bottom: -12px;
  left: 50%;
  z-index: 2;
  transform: translateX(-50%);
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;
}

.btn-urgente {
  color: white;
  background-color: $red-mayday;
  cursor: pointer;

  &:hover {
    background-color: $red-6-mayday;
  }

  &:active {
    background-color: $red-7-mayday;
  }
}
</style>
